import { useCallback, useState } from 'react';
import { workspaceHttp } from '@/lib/api/http-client';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';

async function downloadArtifactPdf(
  projectId: string,
  artifactId: string,
  artifactTitle: string,
): Promise<void> {
  const baseUrl = workspaceHttp.getConfig().baseURL ?? '';
  const response = await workspaceHttp.instance.get<ArrayBuffer>(
    `${baseUrl}/api/v1/projects/${projectId}/artifacts/${artifactId}/export`,
    { responseType: 'arraybuffer', withCredentials: true },
  );

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  // eslint-disable-next-line i18next/no-literal-string -- download filename slug, sanitized for the file system
  const sanitizedTitle = artifactTitle.replace(/[^a-zA-Z0-9-_ ]/g, '').trim() || 'brief';
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- DOM createElement, not React's
  const anchor = document.createElement('a');
  Object.assign(anchor, { href: blobUrl, download: `${sanitizedTitle}.pdf` });
  anchor.click();
  URL.revokeObjectURL(blobUrl);
}

interface UseExportArtifactPdfParams {
  projectId: string;
  artifactId: string;
  artifactTitle: string;
}

export function useExportArtifactPdf({
  projectId,
  artifactId,
  artifactTitle,
}: UseExportArtifactPdfParams) {
  const { t } = useTranslation('projects');

  const [isExporting, setIsExporting] = useState(false);

  const exportPdf = useCallback(async () => {
    setIsExporting(true);
    try {
      await downloadArtifactPdf(projectId, artifactId, artifactTitle);
    } catch {
      notificationService.showError(t('projectArtifact.useExportArtifactPdf.failedToExportPdf'));
    } finally {
      setIsExporting(false);
    }
  }, [projectId, artifactId, artifactTitle, t]);

  return { exportPdf, isExporting };
}
